import { SEARCH_EMPLOYEES } from '@/api/master-data/employee/employee';
import { GET_TRAINING_METHOD } from '@/api/master-data/training-method/training-method';

const state = {
  employeeOptions: [],
  trainingMethodOptions: [],
  loading: false,
  loadingMethods: false,
  error: null
};

const mutations = {
  SET_EMPLOYEE_OPTIONS(state, employees) {
    state.employeeOptions = employees;
  },
  SET_TRAINING_METHOD_OPTIONS(state, methods) {
    state.trainingMethodOptions = methods;
  },
  SET_LOADING(state, status) {
    state.loading = status;
  },
  SET_LOADING_METHODS(state, status) {
    state.loadingMethods = status;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
  CLEAR_EMPLOYEE_OPTIONS(state) {
    state.employeeOptions = [];
  }
};

const actions = {
  async searchEmployees({ commit }, searchQuery) {
    if (!searchQuery || searchQuery.length < 2) {
      commit('CLEAR_EMPLOYEE_OPTIONS');
      return;
    }

    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const response = await SEARCH_EMPLOYEES(searchQuery);

      if (response.data.httpCode === 200) {
        const transformedEmployees = response.data.data.map(employee => ({
          value: employee.nik,
          label: `${employee.full_name} - ${employee.nik}`,
          full_name: employee.full_name,
          nik: employee.nik,
          position: employee.position?.name || '-',
          rawData: employee
        }));

        commit('SET_EMPLOYEE_OPTIONS', transformedEmployees);
      } else {
        throw new Error(response.data.message || 'Failed to fetch employees');
      }
    } catch (error) {
      console.error('Error searching employees:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch employees');
      commit('CLEAR_EMPLOYEE_OPTIONS');
    } finally {
      commit('SET_LOADING', false);
    }
  },

  clearEmployeeOptions({ commit }) {
    commit('CLEAR_EMPLOYEE_OPTIONS');
  },

  async getTrainingMethods({ commit }) {
    commit('SET_LOADING_METHODS', true);
    commit('CLEAR_ERROR');

    try {
      const response = await GET_TRAINING_METHOD();
      if (response.data.httpCode === 200) {
        const methodLabelMap = {
          'online learning': 'In House Online',
          'in-class learning': 'In House Offline',
          'in class learning': 'In House Offline',
          'in-house learning': 'Public Offline',
          'in house learning': 'Public Offline',
          'public online': 'Public Online',
          'public online learning': 'Public Online',
          'self learning': 'Self Paced'
        };
        const methodOrder = [
          'In House Online',
          'In House Offline',
          'Public Online',
          'Public Offline',
          'Self Paced'
        ];

        const transformedMethods = response.data.data
          .map(method => {
            const normalizedName = String(method.name || '').trim().toLowerCase();
            const mappedLabel = methodLabelMap[normalizedName];

            return mappedLabel ? {
              value: method.id,
              label: mappedLabel
            } : null;
          })
          .filter(Boolean)
          .sort((a, b) => methodOrder.indexOf(a.label) - methodOrder.indexOf(b.label));

        commit('SET_TRAINING_METHOD_OPTIONS', transformedMethods);
      } else {
        throw new Error(response.data.message || 'Failed to fetch training methods');
      }
    } catch (error) {
      console.error('Error fetching training methods:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch training methods');
    } finally {
      commit('SET_LOADING_METHODS', false);
    }
  }
};

const getters = {
  getEmployeeOptions: state => state.employeeOptions,
  getTrainingMethodOptions: state => state.trainingMethodOptions,
  getLoading: state => state.loading,
  getLoadingMethods: state => state.loadingMethods,
  getError: state => state.error
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
